import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import produce from "immer";
import {
  createCaseType,
  fetchCaseType,
  updateCaseType,
} from "../../api/case/type";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  system: {},
  data: [],
  currentCount: 0,
  totalCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const updateCaseTypeAsync = createAsyncThunk(
  "cases/updateCaseTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateCaseType(payload);
      const body = response?.data ?? response;
      if (
        body?.error ||
        hasMeaningfulErrors(body?.errors) ||
        isHttpFailureStatus(response?.status)
      ) {
        return rejectNormalized(rejectWithValue, body, "update_case_type");
      }
      return body;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_case_type");
    }
  }
);

export const createCaseTypeAsync = createAsyncThunk(
  "cases/createCaseTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createCaseType(payload);
      const body = response?.data ?? response;
      if (
        body?.error ||
        hasMeaningfulErrors(body?.errors) ||
        isHttpFailureStatus(response?.status)
      ) {
        return rejectNormalized(rejectWithValue, body, "create_case_type");
      }
      return body;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_case_type");
    }
  }
);

export const getCasesTypeAsync = createAsyncThunk(
  "cases/getCasesTypeAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchCaseType(controller);
      const body = response?.data ?? response;
      const status = response?.status ?? body?.status;
      if (
        isHttpFailureStatus(status) ||
        body?.error ||
        hasMeaningfulErrors(body?.errors)
      ) {
        return rejectNormalized(rejectWithValue, body, "get_case_type");
      }
      return body;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_case_type");
    }
  }
);

const CaseTypeSlice = createSlice({
  name: "cases",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCasesTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })

      .addCase(getCasesTypeAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newCount = action.payload.newCount;
      })

      .addCase(getCasesTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente sakstypen.");
      })
      .addCase(updateCaseTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateCaseTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          draftState.data[index] = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateCaseTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere sakstypen.");
      })
      .addCase(createCaseTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createCaseTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createCaseTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette sakstypen.");
      });
  },
});

export const { reducer } = CaseTypeSlice;

export default CaseTypeSlice;
